import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { getAssetUrl } from '@/modules/case/utils/asset-loader';
import { OnboardingCarousel } from '@/modules/onboarding';
import { VideoModal } from '@/modules/video-modal';

import { CaseCard } from './CaseCard';
import { CertificationModal } from './CertificationModal';
import defaultConfig from '../config/selector-config.json';
import { useCaseDetails } from '../hooks/useCaseDetails';
import { useCaseSelector } from '../hooks/useCaseSelector';
import { CaseSelectorProps, CaseSelectorConfig } from '../types';

export const CaseSelector = ({ config: configOverride, masterclassId }: CaseSelectorProps) => {
  const navigate = useNavigate();

  // Navigate back to masterclass home (list of all masterclasses)
  const handleBackToHome = () => {
    navigate(ROUTES.MASTERCLASS_HOME);
  };

  // Merge default config with overrides
  const config = {
    ...defaultConfig,
    ...configOverride,
    // NOTE: It is ok to cast here because we don't have a choice to transform the json transition type
    // to the correct type
    display: {
      ...(defaultConfig.display as CaseSelectorConfig['display']),
      ...configOverride?.display,
    },
    header: { ...defaultConfig.header, ...configOverride?.header },
    caseCard: { ...defaultConfig.caseCard, ...configOverride?.caseCard },
    navigation: { ...defaultConfig.navigation, ...configOverride?.navigation },
    modals: { ...defaultConfig.modals, ...configOverride?.modals },
    colors: { ...defaultConfig.colors, ...configOverride?.colors },
  } satisfies CaseSelectorConfig;

  const {
    cases,
    loading: casesLoading,
    error: casesError,
    masterclassTitle,
  } = useCasesConfig({ masterclassId });
  const {
    userId,
    pendingCase,
    certificationCase,
    setCertificationCase,
    showIntroVideo,
    showConfirmationVideo,
    isLoading,
    caseProgress,
    caseProgressLoading,
    handleIntroClose,
    handleCaseSelect,
    handleConfirmationClose,
    handleConfirmationStart,
    handleSignOut,
  } = useCaseSelector(config, masterclassId);

  const {
    isDemo,
    resetCaseProgress,
    setSelectedMasterclass,
    setClientLogoUrl,
    setMasterclassTitle,
  } = useMasterclass();

  // Fetch first case details to get the client logo
  const firstCase = cases[0];
  const { details: firstCaseDetails } = useCaseDetails(firstCase?.id || null);

  // Set selected masterclass, title, and client logo in context when entering
  useEffect(() => {
    if (masterclassId) {
      setSelectedMasterclass(masterclassId);
    }
    if (masterclassTitle) {
      setMasterclassTitle(masterclassTitle);
    }
    return () => {
      setSelectedMasterclass(null);
      setClientLogoUrl(null);
      setMasterclassTitle(null);
    };
  }, [
    masterclassId,
    masterclassTitle,
    setSelectedMasterclass,
    setClientLogoUrl,
    setMasterclassTitle,
  ]);

  // Set client logo URL when first case details load
  useEffect(() => {
    if (firstCaseDetails?.clientLogoPath && firstCase?.assetsBaseUrl) {
      setClientLogoUrl(`${firstCase.assetsBaseUrl}${firstCaseDetails.clientLogoPath}`);
    }
  }, [firstCaseDetails, firstCase?.assetsBaseUrl, setClientLogoUrl]);

  const selectedCaseItem = cases.find((c) => c.id === pendingCase);
  // caseItem.id is now the database ID, so use it directly for useCaseDetails
  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(
    selectedCaseItem?.id || null
  );

  // Derive intro video URL from loaded case details
  const caseIntroVideoUrl = useMemo(() => {
    if (!caseDetails || !selectedCaseItem) return null;

    const introVideoRef = caseDetails.masterclass?.caseIntroVideoRef;
    if (!introVideoRef) return null;

    const asset = caseDetails.assets?.find((a) => a.id === introVideoRef);
    if (!asset) return null;

    return getAssetUrl(asset, selectedCaseItem.assetsBaseUrl);
  }, [caseDetails, selectedCaseItem]);

  // NOTE: if we are in demo mode, we want to reset the case progress on mount
  useEffect(() => {
    if (!isDemo || !masterclassId) return;
    console.log('Demo mode detected - resetting case progress');
    cases.forEach((caseItem) => {
      console.log(`Resetting progress for case: ${caseItem.id}`);
      void resetCaseProgress(masterclassId, caseItem.id);
    });
  }, [cases, isDemo, masterclassId, resetCaseProgress]);

  // Loading state for cases (when fetching from GraphQL)
  if (casesLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-accent" />
      </div>
    );
  }

  // Error state for cases (when GraphQL fetch fails)
  if (casesError) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <p className="text-destructive">Failed to load cases. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-8">
        <div className="mx-auto max-w-6xl space-y-8">
          <div>
            <Button
              variant="outline"
              onClick={handleBackToHome}
              className="mb-4 border-livelinx-lightgray100 bg-white text-livelinx-purple200 transition-all duration-200 hover:border-livelinx-purple100 hover:bg-livelinx-purple50 hover:text-livelinx-purple200"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              All Masterclasses
            </Button>
            <h1 className="mb-2 text-3xl font-bold text-card-foreground">
              {masterclassTitle || config.header.title}
            </h1>
            <p className="text-muted-foreground">{config.header.subtitle}</p>
          </div>

          {/* Cases Grid */}
          <div className="mx-auto flex max-w-5xl flex-wrap justify-center gap-6">
            {cases.map((caseItem) => {
              // Progress is now fetched per-card in CaseCard component
              return (
                <CaseCard
                  key={caseItem.id}
                  caseId={caseItem.id}
                  masterclassId={masterclassId}
                  title={caseItem.title}
                  configUrl={caseItem.configUrl}
                  assetBaseUrl={caseItem.assetsBaseUrl}
                  userId={userId}
                  onSelect={(caseId, hasProgress) => void handleCaseSelect(caseId, hasProgress)}
                  onStartCertification={setCertificationCase}
                  config={config}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Intro Onboarding Carousel */}
      {config.modals.showIntroOnFirstVisit && (
        <OnboardingCarousel isOpen={showIntroVideo} onClose={() => void handleIntroClose()} />
      )}

      {/* Confirmation Video Modal */}
      {config.modals.showConfirmationBeforeStart &&
        config.navigation.confirmationVideo.enabled &&
        caseDetails && (
          <VideoModal
            isOpen={showConfirmationVideo}
            onClose={handleConfirmationClose}
            onStart={() => void handleConfirmationStart()}
            title={`${config.navigation.confirmationVideo.modalTitle} "${caseDetails.displayName}"`}
            message={config.navigation.confirmationVideo.modalMessage}
            buttonText={config.navigation.confirmationVideo.buttonText}
            videoUrl={caseIntroVideoUrl || config.navigation.confirmationVideo.videoUrl}
          />
        )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl bg-card p-8 shadow-2xl">
            <Loader2 className="h-12 w-12 animate-spin text-accent" />
            <p className="text-lg font-medium text-card-foreground">
              {config.navigation.loadingText}
            </p>
          </div>
        </div>
      )}

      {/* Certification Test or Review Modal */}
      {certificationCase && userId && (
        <CertificationModal
          caseId={certificationCase}
          masterclassId={masterclassId}
          userId={userId}
          onClose={() => setCertificationCase(null)}
        />
      )}
    </div>
  );
};
